import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import moment from 'moment';
import DeTimeline from '../../components/datamanagement/DeTimeline';
import DeRightSidebar from '../../components/datamanagement/DeRightSidebar';
import { useDataEntry } from '../../queries/dataEntryQueries';

/**
 * EwayPage — Printable EWAY document preview & download page.
 *
 * Route: /business/eway/:id
 * Active Step in Timeline: 3 (EWAY)
 */
const EwayPage = () => {
  const { id } = useParams();
  const { data: retrievedData, isLoading, isError } = useDataEntry(id);
  const [isProcessing, setIsProcessing] = useState(false);

  const formatDate = (dateVal) => {
    if (!dateVal) return '-';
    const m = moment(dateVal);
    return m.isValid() ? m.format('DD-MM-YYYY') : dateVal;
  };

  const handleDownloadPdf = () => {
    const element = document.getElementById('eway_download');
    if (!element) return;

    setIsProcessing(true);

    const opt = {
      margin: [6, 6, 6, 6],
      filename: `Eway_${retrievedData?.fileName || id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
        logging: false,
        // windowWidth 760 = A4 portrait full width (794) minus Bootstrap container padding (~32px)
        windowWidth: 760,
        onclone: (clonedDoc) => {
          const root = clonedDoc.getElementById('eway_download');
          if (!root) return;

          // 1. Remove overflow so html2canvas captures the FULL table width
          const wrapper = clonedDoc.querySelector('.eway-table-wrapper');
          if (wrapper) {
            wrapper.style.overflow = 'visible';
            wrapper.style.overflowX = 'visible';
          }

          // 2. Inject <style> tag — base overrides for Bootstrap external CSS (not loaded in clone)
          const style = clonedDoc.createElement('style');
          style.textContent = `
            #eway_download { padding: 6px !important; font-size: 12px !important; }
            #eway_download h5 { font-size: 16px !important; margin-bottom: 4px !important; }
            #eway_download .eway-header-title { font-size: 16px !important; }
            #eway_download span, #eway_download p, #eway_download div { font-size: 12px !important; }
            .eway-table-wrapper { overflow: visible !important; }
            .eway-table { border-collapse: collapse !important; width: 100% !important; }
            .eway-table th { font-size: 10.5px !important; font-weight: bold !important; border: 0.5px solid #888 !important; padding: 5px 2px !important; }
            .eway-table td { font-size: 10.5px !important; border: 0.5px solid #888 !important; padding: 5px 2px !important; }
          `;
          clonedDoc.head.appendChild(style);

          // 3. setProperty with 'important' flag = highest possible specificity (overrides everything)
          root.style.setProperty('padding', '6px', 'important');

          const h5El = root.querySelector('h5');
          if (h5El) h5El.style.setProperty('font-size', '16px', 'important');

          // Filename span (sits next to h5 in the header row) — 16px
          const headerRow = root.querySelector('.border-bottom');
          if (headerRow) {
            const fileNameSpan = headerRow.querySelector('span');
            if (fileNameSpan) fileNameSpan.style.setProperty('font-size', '16px', 'important');
          }

          root.querySelectorAll('span, p, label').forEach((el) => {
            // skip the filename span — already set to 14px above
            el.style.setProperty('font-size', '12px', 'important');
          });

          // Re-apply 16px to h5 and filename span after the loop (loop may have overwritten)
          if (h5El) h5El.style.setProperty('font-size', '16px', 'important');
          if (headerRow) {
            const fileNameSpan = headerRow.querySelector('span');
            if (fileNameSpan) fileNameSpan.style.setProperty('font-size', '16px', 'important');
          }

          // Table header cells — 10.5px bold
          clonedDoc.querySelectorAll('.eway-table th').forEach((th) => {
            th.style.setProperty('font-size', '10.5px', 'important');
            th.style.setProperty('font-weight', 'bold', 'important');
            th.style.setProperty('border', '0.5px solid #888', 'important');
            th.style.setProperty('padding', '5px 2px', 'important');
          });

          // Table data cells — 10.5px
          clonedDoc.querySelectorAll('.eway-table td').forEach((td) => {
            td.style.setProperty('font-size', '10.5px', 'important');
            td.style.setProperty('border', '0.5px solid #888', 'important');
            td.style.setProperty('padding', '5px 2px', 'important');
          });

          const table = clonedDoc.querySelector('.eway-table');
          if (table) {
            table.style.setProperty('border-collapse', 'collapse', 'important');
            table.style.setProperty('width', '100%', 'important');
          }
        },
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .save()
      .then(() => setIsProcessing(false))
      .catch(() => setIsProcessing(false));
  };

  // Extract container items array safely
  const containers = Array.isArray(retrievedData?.userForm) && retrievedData.userForm.length > 0
    ? retrievedData.userForm
    : Array.isArray(retrievedData?.dataEntryForm) && retrievedData.dataEntryForm.length > 0
      ? retrievedData.dataEntryForm
      : [];

  const invoiceRate = Number(retrievedData?.invoiceRate) || 0;
  const customsDuty = Number(retrievedData?.customsDuty) || 0;
  const socialWelfareCharges = Number(retrievedData?.socialWelfareCharges) || 0;
  const assessValue = Number(retrievedData?.assessValue) || 0;
  const invoiceQuantity = Number(retrievedData?.invoiceQuantity) || 1; // avoid division by 0

  // Calculate row taxable value helper
  const calculateTaxable = (plCbmVal) => {
    const plCbm = Number(plCbmVal) || 0;
    return ((customsDuty + socialWelfareCharges + assessValue) / invoiceQuantity) * plCbm;
  };

  // Totals calculations
  const totalNos = containers.reduce((sum, item) => sum + (Number(item?.pcs) || 0), 0);
  const totalBlCbm = containers.reduce((sum, item) => sum + (Number(item?.plCbm) || 0), 0);
  const totalAmt = containers.reduce((sum, item) => sum + ((Number(item?.plCbm) || 0) * invoiceRate), 0);
  const totalTaxable = containers.reduce((sum, item) => sum + calculateTaxable(item?.plCbm), 0);

  return (
    <div className="d-flex flex-column h-100 overflow-hidden overflow-y-auto py-2 py-md-3 px-2 px-md-4">
      <div
        className="d-flex flex-column flex-grow-1 mx-auto w-100"
        style={{ maxWidth: '1280px' }}
      >
        {/* ── Step Progress Timeline — Step 3 active (EWAY) ── */}
        <div className="flex-shrink-0 pb-1">
          <DeTimeline activeStep={3} />
        </div>

        {/* ── Bootstrap 5 two-column layout ── */}
        <div className="row g-3 flex-grow-1 overflow-hidden">
          {/* ── Main Document View (Left Column) ── */}
          <div className="col-12 col-lg-9 d-flex flex-column align-items-center">
            {isLoading ? (
              <div className="card shadow-sm w-100 p-5 text-center text-muted">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                Loading EWAY document data...
              </div>
            ) : isError ? (
              <div className="card shadow-sm w-100 p-5 text-center text-danger">
                Failed to load document data. Please try again.
              </div>
            ) : (
              <div className="w-100 d-flex flex-column align-items-center">
                {/* ── Printable PDF Document Area ── */}
                <div id="eway_download" className="eway-container bg-white border rounded-3 p-3 p-sm-4 shadow-sm w-100 d-flex flex-column" style={{ minHeight: 'calc(100vh - 180px)' }}>
                  {/* File Name Header */}
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3 border-bottom pb-2">
                    <h5 className="fw-bold text-uppercase text-primary mb-0">E-Way Bill Details</h5>
                    <span className="fw-bold fs-5 text-dark font-monospace text-break">
                      {retrievedData?.fileName || '-'}
                    </span>
                  </div>

                  {/* Document Key-Value Details Grid */}
                  <div className="row g-2 mb-4 eway-meta-grid">
                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center">
                        <span className="eway-meta-label">BE NO :</span>
                        <span className="eway-meta-value">{retrievedData?.beNo || '-'}</span>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center">
                        <span className="eway-meta-label">BE DATE :</span>
                        <span className="eway-meta-value">{formatDate(retrievedData?.beDate)}</span>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center">
                        <span className="eway-meta-label">EXPORTER :</span>
                        <span className="eway-meta-value">{retrievedData?.exporter || 'NULL'}</span>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center">
                        <span className="eway-meta-label">CFS NAME :</span>
                        <span className="eway-meta-value">{retrievedData?.cfsName || 'NULL'}</span>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center">
                        <span className="eway-meta-label">DUTY PAYMENT DATE :</span>
                        <span className="eway-meta-value">{formatDate(retrievedData?.dutyPaymentDate)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Containers Table */}
                  <div className="eway-table-wrapper table-responsive border rounded-2 mb-2" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table className="table table-bordered align-middle text-center mb-0 eway-table" style={{ minWidth: '720px', tableLayout: 'fixed', width: '100%' }}>
                      <thead className="gtss-bg-primary text-white text-uppercase">
                        <tr>
                          <th className="text-center" style={{ width: '38px' }}>S.No</th>
                          <th className="text-center" style={{ width: '130px' }}>Container No</th>
                          <th className="text-center" style={{ width: '40px' }}>Nos</th>
                          <th className="text-center" style={{ width: '65px' }}>BL CBM</th>
                          <th className="text-center" style={{ width: '55px' }}>Rate</th>
                          <th className="text-center" style={{ width: '70px' }}>Amt</th>
                          <th className="text-center" style={{ width: '70px' }}>Taxable</th>
                          <th className="text-center" style={{ width: '80px' }}>Lorry No</th>
                          <th className="text-center" style={{ width: '90px' }}>Avg CFT/Girth</th>
                        </tr>
                      </thead>
                      <tbody>
                        {containers.length > 0 ? (
                          containers.map((row, index) => {
                            const plCbmNum = Number(row?.plCbm) || 0;
                            const amt = plCbmNum * invoiceRate;
                            const taxable = calculateTaxable(plCbmNum);

                            return (
                              <tr key={index}>
                                <td className="text-center fw-semibold">{index + 1}</td>
                                <td className="text-center font-monospace fw-bold text-primary">
                                  {row?.containerNo || '-'}
                                </td>
                                <td className="text-center">{row?.pcs || 0}</td>
                                <td className="text-center font-monospace">{plCbmNum.toFixed(3)}</td>
                                <td className="text-center font-monospace">{invoiceRate.toFixed(2)}</td>
                                <td className="text-center font-monospace fw-semibold">{amt.toFixed(2)}</td>
                                <td className="text-center font-monospace">{taxable.toFixed(2)}</td>
                                <td className="text-center">{row?.lorry || '-'}</td>
                                <td className="text-center font-monospace">{row?.avgCftAvgGirth || 0}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-4 text-muted">
                              No container records found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="gtss-bg-primary text-white fw-bold">
                        <tr>
                          <td className="text-center">Total</td>
                          <td></td>
                          <td className="text-center">{totalNos}</td>
                          <td className="text-center font-monospace">{totalBlCbm.toFixed(3)}</td>
                          <td></td>
                          <td className="text-center font-monospace">{totalAmt.toFixed(2)}</td>
                          <td className="text-center font-monospace">{totalTaxable.toFixed(2)}</td>
                          <td></td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Download Button */}
                <div className="my-4 text-center">
                  <button
                    type="button"
                    className="btn btn-orange text-uppercase fw-bold px-4 py-2"
                    onClick={handleDownloadPdf}
                    disabled={isProcessing || isLoading}
                  >
                    {isProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Generating PDF...
                      </>
                    ) : (
                      <>📥 Download PDF</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Right Sidebar Column ── */}
          <div className="col-12 col-lg-3">
            <DeRightSidebar id={id} activeStep={3} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EwayPage;
